export const EMPTY_TIMELINE = { kind: "unknown", duration: 0, ranges: [], liveTarget: null };

export const readRanges = (ranges) => {
  const result = [];
  for (let i = 0; i < (ranges?.length || 0); i += 1) {
    const start = ranges.start(i), end = ranges.end(i);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) result.push({ start, end });
  }
  return result;
};

// Absolute seeks use the closest legal edge; a tie goes forward. Relative
// seeks cross a discontinuity in the requested direction rather than stalling.
export const clampToRanges = (seconds, ranges, direction = 0) => {
  if (!ranges.length) return Math.max(0, seconds);
  for (let i = 0; i < ranges.length; i += 1) {
    const range = ranges[i];
    if (seconds >= range.start && seconds <= range.end) return seconds;
    if (seconds < range.start) {
      const previous = ranges[i - 1];
      if (!previous || direction > 0) return range.start;
      if (direction < 0) return previous.end;
      return seconds - previous.end < range.start - seconds ? previous.end : range.start;
    }
  }
  return ranges[ranges.length - 1].end;
};

export const readTimeline = (player, sourceKind = "file") => {
  if (!player) return EMPTY_TIMELINE;
  const rawDuration = player.getDuration?.();
  const duration = Number.isFinite(rawDuration) && rawDuration > 0 ? rawDuration : 0;
  if (sourceKind === "youtube-live") return { kind: "youtube-live", duration: 0, ranges: [], liveTarget: null };
  if (sourceKind === "youtube-vod") return { kind: duration ? "vod" : "unknown", duration, ranges: duration ? [{ start: 0, end: duration }] : [], liveTarget: null };
  const media = player.getInternalPlayer?.();
  const hls = player.getInternalPlayer?.("hls");
  const details = hls?.levels?.[hls.loadLevel]?.details ?? hls?.levels?.[hls.currentLevel]?.details;
  if (hls && !details) return EMPTY_TIMELINE;
  const live = typeof details?.live === "boolean" ? details.live : media?.duration === Infinity;
  const first = details?.fragments?.[0]?.start;
  const last = details?.fragments?.[details.fragments.length - 1];
  const edge = details?.edge ?? last?.end ?? (last ? last.start + last.duration : NaN);
  const hasPlaylist = Number.isFinite(first) && Number.isFinite(edge) && edge > first;
  if (!live) {
    // ENDLIST does not restore segments removed by a previously sliding DVR.
    // A complete VOD playlist still exposes its full [0, duration] range.
    const ranges = hls
      ? hasPlaylist ? [{ start: Math.max(0, first), end: Math.min(duration || edge, edge) }].filter((r) => r.end > r.start) : []
      : duration ? [{ start: 0, end: duration }] : [];
    return { kind: ranges.length ? "vod" : "unknown", duration, ranges, liveTarget: null };
  }
  let ranges = readRanges(media?.seekable);
  if (hls) {
    // A finite HLS media duration often begins at zero even after its playlist
    // has slid. Never offer those expired segments as a DVR window.
    ranges = hasPlaylist
      ? ranges.map((r) => ({ start: Math.max(r.start, first), end: Math.min(r.end, edge) })).filter((r) => r.end > r.start)
      : [];
  }
  const lastRange = ranges[ranges.length - 1];
  let liveTarget = lastRange == null ? null : clampToRanges(Number.isFinite(hls?.liveSyncPosition) ? hls.liveSyncPosition : lastRange.end, ranges);
  const targetRange = ranges.find((r) => liveTarget >= r.start && liveTarget <= r.end);
  // The exclusive seekable edge may not contain a decodable frame. Keep the
  // displayed bounds exact, but land just inside when returning to live.
  if (targetRange && liveTarget === targetRange.end) liveTarget -= Math.min(0.1, (targetRange.end - targetRange.start) / 2);
  return { kind: "live", duration, ranges, liveTarget };
};
